import React from 'react';

import { Box, HStack, IconButton } from '@chakra-ui/react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from '@hello-pangea/dnd';
import { LuGripVertical } from 'react-icons/lu';

import { IconTrash } from '@/components/Icons';

import { type CheckBoxOption } from './types';

type DraggableCheckboxListProps = {
  options: Array<CheckBoxOption>;
  onReorder: (startIndex: number, endIndex: number) => void;
  renderCheckbox: (option: CheckBoxOption, index: number) => React.ReactNode;
  onDelete?: (option: CheckBoxOption) => void;
};

export const DraggableCheckboxList: React.FC<DraggableCheckboxListProps> = ({
  options,
  onReorder,
  renderCheckbox,
  onDelete,
}) => {
  const handleDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;
    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    if (startIndex !== endIndex) {
      onReorder(startIndex, endIndex);
    }
  };

  const sortedOptions = options.sort((a, b) =>
    a.value === 'other' ? 1 : b.value === 'other' ? -1 : 0
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="checkbox-list">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            bg={snapshot.isDraggingOver ? 'gray.50' : 'transparent'}
            borderRadius="md"
            transition="background-color 0.2s"
          >
            {sortedOptions.map((option, index) => (
              <Draggable
                key={option.value}
                draggableId={option.value ?? `option-${index}`}
                index={index}
                isDragDisabled={option.value === 'other'}
              >
                {(provided, snapshot) => (
                  <HStack
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    spacing={2}
                    align="center"
                    justify="space-between"
                    bg={snapshot.isDragging ? 'white' : 'transparent'}
                    borderRadius="md"
                    p={2}
                    mb={1}
                    boxShadow={
                      snapshot.isDragging
                        ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                        : 'none'
                    }
                  >
                    {/* Checkbox */}
                    <Box flex="1">{renderCheckbox(option, index)}</Box>

                    {/* Drag Handle */}
                    {option.value !== 'other' && (
                      <Box
                        {...provided.dragHandleProps}
                        cursor="grab"
                        border="1px solid"
                        borderColor="gray.100"
                        borderRadius="xl"
                        p={2}
                      >
                        <LuGripVertical fontSize="20px" color="gray.25" />
                      </Box>
                    )}

                    {/* Trash Icon */}
                    {onDelete && (
                      <IconButton
                        aria-label="Delete option"
                        icon={<IconTrash fontSize="16px" />}
                        size="sm"
                        variant="unstyled"
                        color="red.500"
                        shadow="none"
                        onClick={() => onDelete(option)}
                      />
                    )}
                  </HStack>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
